import { OrbitControls, Text3D, Center, useMatcapTexture, Html, Float, Text } from '@react-three/drei'
// import { Perf } from 'r3f-perf'
import { useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import React from 'react'
import * as THREE from 'three'

export default function Experience()
{

    const cube = useRef()

    // const useMatcapTexture = useMatcapTexture('7B524_E9DCC7_B19986_C8AC91')
    const [matcapTexture] = useMatcapTexture('B4B29D_442D0D_604E2A_736542', 512)
    const [matcapTexture1] = useMatcapTexture('7C584C_27140D_B3765C_3D2318', 512)
    const textureLoader = useMemo(() => new THREE.TextureLoader(), [])
    const miniTextures = useMemo(() => {
        const earthMap = textureLoader.load('./textures/00_earthmap1k.jpg')
        const earthBump = textureLoader.load('./textures/01_earthbump1k.jpg')
        const moonMap = textureLoader.load('./textures/06_moonmap4k.jpg')
        const moonBump = textureLoader.load('./textures/07_moonbump4k.jpg')

        ;[earthMap, moonMap].forEach((tex) => {
            tex.anisotropy = 8
            tex.minFilter = THREE.LinearMipmapLinearFilter
            tex.magFilter = THREE.LinearFilter
            tex.generateMipmaps = true
        })

        return { earthMap, earthBump, moonMap, moonBump }
    }, [textureLoader])

    console.log(matcapTexture)

    const { viewport } = useThree()
    const isMobile = viewport.width < 6
    const titleSize = isMobile ? 0.65 : 0.75
    const titleHeight = isMobile ? 0.16 : 0.2
    const subtitleSize = isMobile ? 0.25 : 0.4

    const titleGroupRef = useRef()

    const cubeFireflies = useMemo(() => {
        const count = 80
        const positions = new Float32Array(count * 3)
        const basePositions = new Float32Array(count * 3)
        const sizes = new Float32Array(count)
        for (let i = 0; i < count; i += 1)
        {
            const x = (Math.random() - 0.5) * 0.9
            const y = (Math.random() - 0.5) * 0.9
            const z = (Math.random() - 0.5) * 0.9
            positions[i * 3 + 0] = x
            positions[i * 3 + 1] = y
            positions[i * 3 + 2] = z
            basePositions[i * 3 + 0] = x
            basePositions[i * 3 + 1] = y
            basePositions[i * 3 + 2] = z
            sizes[i] = 0.5 + Math.random() * 1.2
        }
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('aScale', new THREE.BufferAttribute(sizes, 1))
        const material = new THREE.ShaderMaterial({
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            uniforms: {
                uSize: { value: 18 },
                uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
                uColor: { value: new THREE.Color('#ffffff') }
            },
            vertexShader: `
                uniform float uSize;
                uniform float uPixelRatio;
                attribute float aScale;

                void main() {
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    gl_PointSize = uSize * aScale * uPixelRatio * (1.0 / -mvPosition.z);
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                void main() {
                    float d = distance(gl_PointCoord, vec2(0.5));
                    float alpha = smoothstep(0.5, 0.0, d);
                    gl_FragColor = vec4(uColor, alpha);
                }
            `
        })
        return { geometry, material, basePositions, count, sizes }
    }, [])

    const firefliesRef = useRef()

    const miniSystemRef = useRef()
    const miniOrbitRef = useRef()
    const miniMoonOrbitRef = useRef()
    const miniSunRef = useRef()
    const sunShaderMaterial = useMemo(() => new THREE.ShaderMaterial({
        transparent: false,
        depthWrite: true,
        depthTest: true,
        uniforms: {
            uTime: { value: 0 },
            uColorInner: { value: new THREE.Color('#fff1c9') },
            uColorMid: { value: new THREE.Color('#ffb347') },
            uColorOuter: { value: new THREE.Color('#ff6b2a') }
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform float uTime;
            uniform vec3 uColorInner;
            uniform vec3 uColorMid;
            uniform vec3 uColorOuter;
            varying vec2 vUv;

            float hash(vec2 p) {
                return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
            }

            float noise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                float a = hash(i);
                float b = hash(i + vec2(1.0, 0.0));
                float c = hash(i + vec2(0.0, 1.0));
                float d = hash(i + vec2(1.0, 1.0));
                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
            }

            float fbm(vec2 p) {
                float v = 0.0;
                float a = 0.5;
                for (int i = 0; i < 4; i++) {
                    v += a * noise(p);
                    p *= 2.0;
                    a *= 0.5;
                }
                return v;
            }

            void main() {
                vec2 uv = vUv - 0.5;
                float r = length(uv) * 2.0;
                float n = fbm(uv * 6.0 + uTime * 0.2);
                float edge = smoothstep(1.0, 0.2, r);
                float bands = smoothstep(0.9, 0.2, r + n * 0.2);
                vec3 inner = mix(uColorMid, uColorInner, edge);
                vec3 color = mix(uColorOuter, inner, bands);
                gl_FragColor = vec4(color, 1.0);
            }
        `
    }), [])
    const miniEarthRef = useRef()
    const miniMoonRef = useRef()

    useFrame((state, delta) =>
    {
        cube.current.rotation.y += delta * 0.2
        if (firefliesRef.current)
        {
            const { array } = firefliesRef.current.geometry.attributes.position
            const { basePositions, count } = cubeFireflies
            const t = state.clock.elapsedTime
            for (let i = 0; i < count; i += 1)
            {
                const idx = i * 3
                const jitter = 0.02
                array[idx] = basePositions[idx] + Math.sin(t * 2 + i) * jitter
                array[idx + 1] = basePositions[idx + 1] + Math.cos(t * 2.5 + i) * jitter
                array[idx + 2] = basePositions[idx + 2] + Math.sin(t * 3 + i) * jitter
            }
            firefliesRef.current.geometry.attributes.position.needsUpdate = true
        }
        if (miniSystemRef.current && miniOrbitRef.current && miniMoonOrbitRef.current && miniEarthRef.current && miniMoonRef.current)
        {
            const t = state.clock.elapsedTime
            miniSystemRef.current.rotation.y += delta * 0.15
            miniOrbitRef.current.rotation.y += delta * 0.35
            miniMoonOrbitRef.current.rotation.y += delta * 1.2
            miniEarthRef.current.rotation.y += delta * 0.6
        }
        sunShaderMaterial.uniforms.uTime.value = state.clock.elapsedTime
    })



    return <>

        {/* <Perf position="top-left" /> */}

        <OrbitControls makeDefault />

        <directionalLight position={ [ 1, 2, 3 ] } intensity={ 1.5 } />
        <ambientLight intensity={0.5} />

        

        <mesh
            scale={1.5}
            position={isMobile ? [0, -0.4, 0] : [-4.2, 0, 0]}
            ref={cube}
           
        >
            <boxGeometry/>
            <meshMatcapMaterial matcap={matcapTexture1} wireframe={true} />
            <pointLight intensity={1.2} distance={3} color={'#ffffff'} />
            <points ref={firefliesRef} geometry={cubeFireflies.geometry} material={cubeFireflies.material} />
            <group ref={miniSystemRef} position={[0, 0, 0]}>
                <group ref={miniSunRef} position={[0, 0, 0]}>
                    <mesh scale={0.16}>
                        <sphereGeometry args={[1, 48, 48]} />
                        <primitive object={sunShaderMaterial} attach="material" />
                    </mesh>
                    <mesh scale={0.22}>
                        <sphereGeometry args={[1, 48, 48]} />
                        <meshBasicMaterial color={'#ffd9a8'} transparent opacity={0.22} depthWrite={false} />
                    </mesh>
                    <mesh scale={0.28}>
                        <sphereGeometry args={[1, 48, 48]} />
                        <meshBasicMaterial color={'#ffb347'} transparent opacity={0.18} depthWrite={false} />
                    </mesh>
                </group>
                <group ref={miniOrbitRef}>
                    <mesh ref={miniEarthRef} position={[0.36, 0, 0]} scale={0.11}>
                        <sphereGeometry args={[1, 24, 24]} />
                        <meshStandardMaterial
                            map={miniTextures.earthMap}
                            bumpMap={miniTextures.earthBump}
                            bumpScale={0.02}
                            transparent={false}
                            opacity={1}
                        />
                        <group ref={miniMoonOrbitRef}>
                            <mesh ref={miniMoonRef} position={[0.52, 0.08, 1.2]} scale={0.32} renderOrder={10}>
                                <sphereGeometry args={[1, 24, 24]} />
                                <meshBasicMaterial map={miniTextures.moonMap} transparent={false} opacity={1} />
                            </mesh>
                        </group>
                    </mesh>
                </group>
            </group>
        </mesh>

     

        <group ref={titleGroupRef} position={[0, isMobile ? 1.1 : 0, 0]}>
            <Center
                onCentered={({ width }) => {
                    if (!isMobile) return
                    if (!titleGroupRef.current) return
                    // Fit the text inside the visible viewport on phones.
                    // `width` is the unscaled bounding-box width of the centered content.
                    const maxWorldWidth = viewport.width * 0.92
                    const fitScale = width > 0 ? Math.min(1, maxWorldWidth / width) : 1
                    titleGroupRef.current.scale.set(fitScale, fitScale, fitScale)
                }}
            >
                <Text3D
                    font='./fonts/helvetiker_regular.typeface.json'
                    size={titleSize}
                    height={titleHeight}
                    curveSegments={12}
                    bevelEnabled
                    bevelThickness={0.02}
                    bevelSize={0.02}
                    bevelOffset={0}
                    bevelSegments={5}
                >
                    Aaron Enyetu
                    <meshMatcapMaterial matcap={matcapTexture} />
                </Text3D>
            </Center>
        </group>

        <mesh position-x={ - 2 } >
            
                <Html
                    position={[2, -3, 0]}
                    wrapperClass="label"
                    center
                distanceFactor={6}

                >
                    <a className="enter-3d-btn" href={'./home.html'}>
                        Enter 3D Site
                    </a>
            </Html>
            
            
        </mesh>

        <Float>
                <Text
                color={'#F8F8FF'}
            fontSize={subtitleSize}
               
                position-y={-2}
               
                
                
                
                    
                >
                    Software Engineer
                </Text>
        </Float>


    </>
}